/**
 * HeatHazeMaterial — screen-space refraction shimmer (heat haze, mirages,
 * engine exhaust, shockwaves) drawn on a billboard/volume proxy mesh.
 *
 * Samples the already-rendered scene colour (three r185
 * `viewportSharedTexture` — the grab-pass equivalent, WebGL2 + WebGPU) at a
 * UV offset by animated noise, faded by a radial mask so the quad's edges
 * never show a hard seam. Purely refractive: the material contributes no
 * colour of its own.
 */

import * as THREE from 'three/webgpu';
import {
  Fn, uniform, float, vec2, uv, screenUV, mx_noise_float, time, smoothstep, saturate,
} from 'three/tsl';
import { viewportSharedTexture } from 'three/tsl';

export interface HeatHazeConfig {
  /** Screen-space distortion amplitude (default 0.01). */
  strength?: number;
  /** Noise spatial frequency (default 6). */
  noiseScale?: number;
  /** Upward drift + wobble speed (default 1.5). */
  speed?: number;
  /** Radial edge softness of the proxy quad (default 0.35). */
  edgeSoftness?: number;
}

export interface HeatHazeUniforms {
  strength: { value: number };
  noiseScale: { value: number };
  speed: { value: number };
  edgeSoftness: { value: number };
}

/** CPU mirror of the radial mask (exported for tests). */
export function hazeMaskCpu(u: number, v: number, edgeSoftness: number): number {
  const dx = u - 0.5;
  const dy = v - 0.5;
  const dist = Math.sqrt(dx * dx + dy * dy) * 2; // 0 centre → 1 at the rim
  const t = (1 - dist) / Math.max(edgeSoftness, 1e-6);
  return t < 0 ? 0 : (t > 1 ? 1 : t);
}

export function createHeatHazeMaterial(config?: HeatHazeConfig): {
  material: THREE.NodeMaterial; uniforms: HeatHazeUniforms;
} {
  const uStrength = uniform(config?.strength ?? 0.01);
  const uNoiseScale = uniform(config?.noiseScale ?? 6);
  const uSpeed = uniform(config?.speed ?? 1.5);
  const uEdge = uniform(config?.edgeSoftness ?? 0.35);

  const material = new THREE.MeshBasicNodeMaterial();
  material.transparent = true;
  material.depthWrite = false;
  material.side = THREE.DoubleSide;

  material.colorNode = Fn(() => {
    const u0 = uv();
    // Rising, wobbling noise field — two octaves offset in time.
    const n1 = mx_noise_float(u0.mul(uNoiseScale).add(vec2(0, time.mul(uSpeed).negate())));
    const n2 = mx_noise_float(u0.mul(uNoiseScale).mul(2.3).add(vec2(time.mul(uSpeed).mul(0.7), 0)));
    const offset = vec2(n1, n2).mul(uStrength);
    // Radial mask so the proxy's edges never cut a visible seam.
    const centered = u0.sub(vec2(0.5, 0.5)).mul(2);
    const mask = saturate(smoothstep(float(0), uEdge, float(1).sub(centered.length())));
    const scene = viewportSharedTexture(screenUV.add(offset.mul(mask)));
    return scene;
  })();

  return {
    material: material as THREE.NodeMaterial,
    uniforms: {
      strength: uStrength as unknown as { value: number },
      noiseScale: uNoiseScale as unknown as { value: number },
      speed: uSpeed as unknown as { value: number },
      edgeSoftness: uEdge as unknown as { value: number },
    },
  };
}
